import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getQuebrasBroadcastBot, type BroadcastBot, type FiltrosBot, type QuebraLinha } from '@/services/api/analytics-bot';
import { n, pct, TRACO, usd, usd4 } from '../format';
import Botoes from './Botoes';
import PreviewCopy from './PreviewCopy';
import Avisos from './Avisos';
import EstadoBloco from './EstadoBloco';

interface BroadcastAbertoProps {
    b: BroadcastBot;
    filtros: FiltrosBot;
    rotulo: string;
    onFechar: () => void;
}

const COLUNAS: { k: 'paginas' | 'paises' | 'dominios'; l: string }[] = [
    { k: 'paginas', l: 'Top 5 páginas' },
    { k: 'paises', l: 'Top 5 países' },
    { k: 'dominios', l: 'Top 5 domínios' },
];

/** Criativa renderizada com os números ao lado, botões e as três quebras. */
export default function BroadcastAberto({ b, filtros, rotulo, onFechar }: BroadcastAbertoProps) {
    const quebrasQ = useQuery({
        queryKey: ['analytics-bot', 'quebras', b.id, filtros],
        queryFn: () => getQuebrasBroadcastBot(filtros, b.id),
        placeholderData: keepPreviousData,
        staleTime: 60_000,
    });
    const q = quebrasQ.data;

    const textos = [b.copy?.titulo, b.copy?.texto].filter((t, i, a): t is string => !!t && a.indexOf(t) === i);
    // Criativa inteira do grupo (ex.: bloco de imagem + bloco de texto); sem a lista, cai no 1º bloco.
    const blocos = b.copy?.blocos?.length
        ? b.copy.blocos.map(x => ({
            midiaUrl: x.imagem_url,
            video: x.tipo === 'video',
            texto: [x.titulo, x.texto].filter((t, i, a): t is string => !!t && a.indexOf(t) === i).join('\n\n') || null,
            subtitulo: x.subtitulo,
            botoes: x.botoes.filter((t): t is string => !!t),
        }))
        : null;

    return (
        <div className="exp" onClick={e => e.stopPropagation()}>
            <div className="exp-h">
                <b>Criativa e desempenho<span>{b.nome} · {b.hora} · {rotulo}</span></b>
                <button type="button" className="mini" onClick={onFechar}>fechar</button>
            </div>
            <div className="legenda">Receita e RPC por botão: {TRACO} precisa do rastreio por botão (V2).</div>
            <div className="stp">
                <PreviewCopy
                    copy={blocos ?? (b.copy ? {
                        midiaUrl: b.copy.imagem_url,
                        texto: textos.join('\n\n') || null,
                        subtitulo: b.copy.subtitulo,
                        botoes: b.botoes.map(x => x.label),
                    } : null)}
                    titulo={`${b.nome} · ${b.hora}`}
                    linhas={[
                        `${n(b.enviados)} enviados · ${n(b.entregues)} entregues (${pct(b.taxa_entrega_pct, 1)}) · ${n(b.falhas)} falhas`,
                        `${n(b.cliques)} cliques · ${n(b.clicadores_unicos)} clicadores únicos · CTR ${pct(b.ctr_pct)}`,
                        `Receita ${usd(b.receita)} · RPC ${usd4(b.rpc)}`,
                    ]}
                    utm={`utm_content=broad_${b.id}`}
                />
                <Botoes botoes={b.botoes.map(x => ({
                    id: x.id, label: x.label, destino: 'botão do disparo', tipo: 'outro' as const,
                    cliques: x.cliques, unicos: null, ctr: x.ctr_pct,
                }))} />
            </div>

            {!q ? (
                <EstadoBloco carregando={quebrasQ.isLoading} erro={quebrasQ.isError} onTentar={() => quebrasQ.refetch()} />
            ) : (
                <div className="qb" data-loading={quebrasQ.isPlaceholderData ? 1 : 0}>
                    {COLUNAS.map(c => {
                        const linhas: QuebraLinha[] = (q[c.k] ?? []).slice(0, 5);
                        return (
                            <div className="qb-col" key={c.k}>
                                <div className="qb-h">{c.l}</div>
                                {linhas.length === 0 && <div className="qb-r"><span className="muted">Sem dados.</span></div>}
                                {linhas.map((x, i) => (
                                    <div className="qb-r" key={`${x.nome}-${i}`}>
                                        <span title={x.nome}>{x.nome}<i>{n(x.cliques)} cliques</i></span>
                                        <b>{x.receita == null ? <span className="muted">{TRACO}</span> : usd(x.receita)}</b>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
            <Avisos avisos={q?.avisos} inline />
        </div>
    );
}
